import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, DatePicker, TimePicker, Button, Typography, Row, Col, Alert } from 'antd';
import { useLanguage } from 'hooks/Languages/useLanguage';
import { useFetchUserData } from 'queries/auth/user';
import { getPharmacyStatus, enablePharmacy } from 'services/pharmacy-onboarding';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import Toast from 'components/shared/Alert';
import { notification } from 'antd';
import { 
  ShieldCheck, 
  Award, 
  Clock, 
  FileText, 
  Activity,
  Calendar
} from 'lucide-react';
import './styles.scss';

const { Title, Text } = Typography;

const PharmacyOnboarding = () => {
  const { translate } = useLanguage();
  const { userData } = useFetchUserData();
  const [form] = Form.useForm();
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [api, contextHolder] = notification.useNotification();
  const queryClient = useQueryClient();

  const isPharmacyUser = userData?.business_type?.toLowerCase() === 'pharmacy';

  // Fetch Pharmacy Status
  const { data: statusData, isLoading } = useQuery({
    queryKey: ['pharmacyStatus', userData?.shop_id],
    queryFn: getPharmacyStatus,
    enabled: !!isPharmacyUser,
    retry: false,
  });

  useEffect(() => {
    if (statusData && statusData.enabled === false) {
      setVisible(true);
    } else {
      setVisible(false);
    }
  }, [statusData]);

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);
      const payload = {
        pharmacy_license_number: values.pharmacy_license_number,
        license_issued_date: values.license_issued_date.format('YYYY-MM-DD'),
        license_expiry_date: values.license_expiry_date.format('YYYY-MM-DD'),
        issuing_authority: values.issuing_authority,
        tax_registration_number: values.tax_registration_number,
        default_opening_time: values.default_opening_time.format('HH:mm'),
        default_closing_time: values.default_closing_time.format('HH:mm'),
        offers_prescription_service: true,
        offers_otc_sales: true,
        offers_consultation: false,
      };

      await enablePharmacy(payload);
      
      Toast(api, 'Success', translate('Pharmacy Profile Created Successfully!'));
      setVisible(false);
      
      // Invalidate query to prevent modal from reopening
      queryClient.invalidateQueries({ queryKey: ['pharmacyStatus'] });
      
    } catch (error: any) {
      Toast(api, 'Error', error.message || translate('Failed to create profile'));
    } finally {
      setLoading(false);
    }
  };

  if (!isPharmacyUser) return null;
  if (isLoading) return null; // Or a spinner, but better to be silent
  if (statusData?.enabled) return null;

  return (
    <>
      {contextHolder}
      <Modal
        open={visible}
        title={null}
        footer={null}
        closable={false}
        maskClosable={false}
        width={720}
        centered
        className="premium-onboarding-modal"
      >
        <div className="onboarding-modal-body">
          <div className="onboarding-header">
            <div className="onboarding-logo-badge">
              <Activity size={22} className="pulse-icon" />
            </div>
            <Title level={3} style={{ margin: '0 0 6px 0', fontWeight: 700, color: '#1f1f1f' }}>
              {translate("Let's Set Up Your Pharmacy")}
            </Title>
            <Text className="onboarding-subtitle">
              {translate("Configure your regulatory credentials to enable prescription features.")}
            </Text>
          </div>

          <Alert 
            message={translate("Required Setup: Fill in licensing details to enable prescription tracking.")}
            type="info"
            showIcon
            className="premium-alert-banner"
          />

          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{
              default_opening_time: dayjs('08:00', 'HH:mm'),
              default_closing_time: dayjs('20:00', 'HH:mm'),
            }}
          >
            {/* UNIFIED COMPACT REGULATORY CONTROL CARD */}
            <div className="onboarding-form-section">
              {/* Row 1: Credentials & Licensing */}
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="pharmacy_license_number"
                    label={translate('Pharmacy License Number')}
                    rules={[{ required: true, message: translate('Required') }]}
                  >
                    <Input 
                      placeholder="e.g. PH-12345-REG" 
                      prefix={<Award size={14} />}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="issuing_authority"
                    label={translate('Issuing Authority')}
                    rules={[{ required: true, message: translate('Required') }]}
                  >
                    <Input 
                      placeholder="e.g. Ministry of Health" 
                      prefix={<ShieldCheck size={14} />}
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* Row 2: License Dates */}
              <Row gutter={16} style={{ marginTop: 4 }}>
                <Col span={12}>
                  <Form.Item
                    name="license_issued_date"
                    label={translate('License Issued Date')}
                    rules={[{ required: true, message: translate('Required') }]}
                  >
                    <DatePicker 
                      style={{ width: '100%' }} 
                      placeholder={translate('Select issue date')}
                      suffixIcon={<Calendar size={14} className="text-secondary" />}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="license_expiry_date"
                    label={translate('License Expiry Date')}
                    rules={[{ required: true, message: translate('Required') }]}
                  >
                    <DatePicker 
                      style={{ width: '100%' }} 
                      placeholder={translate('Select expiry date')}
                      suffixIcon={<Calendar size={14} className="text-secondary" />}
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* Row 3: Operating Hours & Tax ID */}
              <Row gutter={16} style={{ marginTop: 4 }}>
                <Col span={6}>
                  <Form.Item
                    name="default_opening_time"
                    label={translate('Opening Time')}
                    rules={[{ required: true }]}
                  >
                    <TimePicker format="HH:mm" style={{ width: '100%' }} placeholder="08:00" />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    name="default_closing_time"
                    label={translate('Closing Time')}
                    rules={[{ required: true }]}
                  >
                    <TimePicker format="HH:mm" style={{ width: '100%' }} placeholder="20:00" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="tax_registration_number"
                    label={translate('Tax / VAT Registration (Optional)')}
                  >
                    <Input 
                      placeholder={translate('Tax ID')} 
                      prefix={<FileText size={14} />}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </div>

            <div style={{ marginTop: 24 }}>
              <Button 
                type="primary" 
                htmlType="submit" 
                size="large" 
                loading={loading} 
                block
                className="btn-activate-pharmacy"
              >
                {translate('Save & Activate Pharmacy Profile')}
              </Button>
            </div>
          </Form>
        </div>
      </Modal>
    </>
  );
};

export default PharmacyOnboarding;
